import { getBffBaseUrl, getVisitKey, LANDLORDS_PATH } from '@/lib/bff/config'

/*
 * BFF client for Landlord management (VIS-328). Landlords are VisitOne staff
 * who act in every tenant, so no call carries a tenant. The SPA offers the
 * screen from the `landlords.manage` action in the caller's access decision
 * (VIS-329); the BFF enforces it and decides, per row, whether a Landlord can
 * be removed or sent the set-password link again. Errors arrive in the
 * `{ "error": "<code>" }` envelope.
 */

export type LandlordRemoval = 'allowed' | 'self' | 'onlyLandlord'

export type Landlord = {
  /** Auth0 user id. */
  id: string
  email: string | null
  name: string | null
  isSelf: boolean
  signedIn: boolean
  canResendLink: boolean
  removal: LandlordRemoval
}

export type LandlordPage = {
  data: Landlord[]
  pagination: { page: number; size: number; totalCount: number; totalPages: number }
}

export type AddLandlordResult = { landlord: Landlord; passwordEmailSent: boolean }

/** The page size the Landlords screen asks for; the BFF caps `size` at 100. */
export const LANDLORD_PAGE_SIZE = 100

/** Raised for a non-2xx response or a network failure; `code` is the BFF's error code when it sent one. */
export class LandlordRequestError extends Error {
  readonly status: number
  readonly code: string | null

  constructor(status: number, code: string | null, message = `Landlord request failed (${status})`) {
    super(message)
    this.name = 'LandlordRequestError'
    this.status = status
    this.code = code
  }
}

function headers(token: string, json = false): HeadersInit {
  return {
    Authorization: `Bearer ${token}`,
    'x-visit-key': getVisitKey(),
    Accept: 'application/json',
    ...(json ? { 'Content-Type': 'application/json' } : {}),
  }
}

const url = (path = '') => `${getBffBaseUrl('mmv')}${LANDLORDS_PATH}${path}`

async function send(input: string, init: RequestInit): Promise<Response> {
  let response: Response
  try {
    response = await fetch(input, init)
  } catch {
    throw new LandlordRequestError(0, null, 'Network error during a Landlord request')
  }
  if (!response.ok) {
    let code: string | null = null
    try {
      const body = (await response.json()) as { error?: unknown }
      if (typeof body?.error === 'string') code = body.error
    } catch {
      code = null
    }
    throw new LandlordRequestError(response.status, code)
  }
  return response
}

async function json<T>(response: Response): Promise<T> {
  try {
    return (await response.json()) as T
  } catch {
    throw new LandlordRequestError(response.status, null, 'Malformed Landlord response')
  }
}

/** One 0-based page of Landlords, sorted by email. */
export async function listLandlords(
  token: string,
  page: number,
  size = LANDLORD_PAGE_SIZE,
  signal?: AbortSignal,
): Promise<LandlordPage> {
  const query = new URLSearchParams({ page: String(page), size: String(size) })
  const response = await send(`${url()}?${query}`, { method: 'GET', headers: headers(token), signal })
  return json<LandlordPage>(response)
}

/** Adds a Landlord by email and sends them a set-password link. */
export async function addLandlord(token: string, email: string): Promise<AddLandlordResult> {
  const response = await send(url(), {
    method: 'POST',
    headers: headers(token, true),
    body: JSON.stringify({ email }),
  })
  return json<AddLandlordResult>(response)
}

export async function removeLandlord(token: string, userId: string): Promise<void> {
  await send(url(`/${encodeURIComponent(userId)}`), { method: 'DELETE', headers: headers(token) })
}

/** Sends a fresh set-password link to a Landlord who has not signed in yet. */
export async function resendLandlordSetPasswordLink(token: string, userId: string): Promise<void> {
  await send(url(`/${encodeURIComponent(userId)}/set-password-link`), {
    method: 'POST',
    headers: headers(token),
  })
}
